import { V1 } from "@itelligo/lib-ui-core";
import jwtDecode from "jwt-decode";
import BaseAPI from "../utils/API/BaseAPI";
import { consoleWarn } from "../utils/log";
import { AccountType } from "../utils/models/Account";
import { normalizeV4Permissions } from "../utils/v4Permissions";
import { isV4ManagerFlagged } from "../utils/v4AccountTree";
import { storedV4AccountLanguage, storedV4AccountReference } from "../utils/v4AccountProfile";
import {
  clearV4Session,
  clearV4SessionScope,
  enterV4SessionScope,
  getV4BootScope,
  getV4OwnerAccount,
  getV4RefreshToken,
  getV4SessionScope,
  getV4Token,
  isOnV4ExcludedPath,
  isV4ManagerScope,
  leaveV4SessionScope,
  setV4BootScope,
  setV4OwnerAccount,
  setV4SessionScope,
  storeV4Token,
  V4_SESSION_EXCLUDED_PATHS,
} from "../utils/v4Session";

// Re-exported so callers keep one V4 entry point; v4Session itself must stay import-free.
export {
  clearV4Session,
  clearV4SessionScope,
  enterV4SessionScope,
  getV4BootScope,
  getV4OwnerAccount,
  getV4SessionScope,
  isOnV4ExcludedPath,
  isV4ManagerScope,
  leaveV4SessionScope,
  setV4BootScope,
  setV4OwnerAccount,
  setV4SessionScope,
  V4_SESSION_EXCLUDED_PATHS,
};

const V4_BASE = process.env.REACT_APP_API_V4_BASE;

const API = new V1({
  baseURL: `${V4_BASE}`,
  // `v4Token` tags what the 401 handler may renew: only a v4User token has a refresh token.
  securityWorker: async () => {
    const v4Token = await freshV4Token();
    return v4Token
      ? { headers: { Authorization: `Bearer ${v4Token}` }, v4Token }
      : { headers: { Authorization: `Bearer ${BaseAPI.token}` } };
  },
});

const SCOPE_HEADER = 'X-Account-Scope';

// Never scoped: these identify the caller, not an account, and /v1/public is
// anonymous. Exact or '/'-suffixed match only — a bare prefix would unpin
// /v1/users too, which IS scoped (to the target account) by the admin calls.
const SCOPELESS_PATHS = ['/v1/authentication', '/v1/user', '/v1/public'];

// Account-owned entities, where "no header" and "my own account" already mean
// the same thing. An allow-list, not a deny-list: /v1/accounts, /v1/permissions
// and /v1/migration/lookup answer *differently* when scoped (the account-manager
// bootstrap reads an unscoped /v1/accounts), and /v1/devices carries global
// registries. A path joins only once the platform is known to accept the header
// on it — every entry here is already sent scoped by the V3 sagas.
const OWNER_SCOPED_PATHS = ['/v1/scenarios', '/v1/teams', '/v1/geofences', '/v1/beacons', '/v1/sites'];

const matchesPath = (url, prefixes) =>
  prefixes.some(p => url === p || url.startsWith(p + '/') || url.startsWith(p + '?'));

// axios hands the interceptor an AxiosHeaders; a hand-built config is a plain object.
const hasScopeHeader = headers =>
  typeof headers?.has === 'function' ? headers.has(SCOPE_HEADER) : !!headers?.[SCOPE_HEADER];

const setHeader = (config, name, value) => {
  if (typeof config.headers?.set === 'function') config.headers.set(name, value);
  else config.headers = { ...(config.headers || {}), [name]: value };
};

/**
 * The one place that decides which account a V4 request acts on — the securityWorker
 * above supplies the JWT, this supplies the scope that goes with it. In order:
 * an explicit per-call header (cross-account admin work must win), the sub-account
 * a manager entered, the V4-native user's own account on entity paths, nothing.
 *
 * Synchronous by design: awaiting a lookup here would recurse through /v1/user.
 */
function resolveV4Scope(url, headers) {
  // Public/ARC pages own their session — a lingering manager scope must not bleed in.
  if (isOnV4ExcludedPath()) return null;
  if (matchesPath(url, SCOPELESS_PATHS)) return null;
  if (hasScopeHeader(headers)) return null;
  const entered = getV4SessionScope()?.accountId;
  if (entered) return entered;
  return matchesPath(url, OWNER_SCOPED_PATHS) ? getV4OwnerAccount() : null;
}

API.instance.interceptors.request.use(config => {
  if (config.v4Unscoped) return config;
  const scope = resolveV4Scope(config.url || '', config.headers);
  if (!scope) return config;
  setHeader(config, SCOPE_HEADER, scope);
  return config;
});

export const isV4TokenOnlySession = () =>
  !!getV4Token() && !localStorage.getItem('user');

// A valid token may simply not be allowed to read these, so a rejection here
// must never be read as an expired session. Geocoding fires from background
// map renders, where a logout would look random; /v1/permissions is granted in
// a fan-out where one refused sub-account would end the session mid-create.
// Beacons and sites need the Location Service, which an account may lack.
const SESSION_SAFE_PATHS = [
  '/v1/accounts',
  '/v1/locations/geocode',
  '/v1/permissions',
  '/v1/beacons',
  '/v1/sites',
];

// Same exemption, one request. A cross-account fan-out needs it: the interceptor runs
// before the caller's own `.catch`, so one refused account would end the session.
export const v4SessionSafe = params => ({ ...params, sessionSafe: true });

// No X-Account-Scope: a scoped /v1/accounts answers with the entered account's parent and siblings.
export const v4Unscoped = params => ({ ...params, v4Unscoped: true });

// Renewed this long before `exp`, so a request in flight doesn't arrive expired.
const V4_RENEW_MARGIN_S = 60;

const isV4TokenExpiring = token => {
  try {
    const { exp } = jwtDecode(token);
    return !!exp && exp - V4_RENEW_MARGIN_S <= Date.now() / 1000;
  } catch (_) {
    return false;
  }
};

let v4Renewal = null;
// A refused renewal would only be refused again.
let v4RenewalRefusedFor = null;

async function requestV4Renewal(token) {
  const refreshToken = getV4RefreshToken();
  if (!refreshToken || token === v4RenewalRefusedFor) return null;
  try {
    // Unsigned, or the securityWorker would wait on this very renewal.
    const res = await API.authenticationRefreshTokenCreate(
      { access_token: token, refresh_token: refreshToken },
      v4SessionSafe({ secure: false }),
    );
    const fresh = res?.data?.access_token;
    if (!fresh) return null;
    // A logout, a new login or another tab's renewal during the call wins.
    if (getV4Token() !== token) return getV4Token();
    storeV4Token(fresh);
    // bootstrapV4Native runs the session on an in-memory copy as well.
    if (BaseAPI.inMemoryToken === token) BaseAPI.inMemoryToken = fresh;
    return fresh;
  } catch (e) {
    // Only a 4xx is final; a network error or 5xx may pass next time.
    const status = e?.response?.status;
    if (status >= 400 && status < 500) v4RenewalRefusedFor = token;
    consoleWarn('V4 token renewal failed', e);
    return null;
  }
}

function renewV4Token(token) {
  if (!v4Renewal) v4Renewal = requestV4Renewal(token).finally(() => { v4Renewal = null; });
  return v4Renewal;
}

export async function freshV4Token() {
  const token = getV4Token();
  if (!token || !isV4TokenExpiring(token)) return token;
  return (await renewV4Token(token)) || getV4Token();
}

// A V3 session's V4 calls may legitimately 401 (unmigrated account) without ending it.
const endsV4Session = config =>
  isV4TokenOnlySession() &&
  // Public/token routes (incl. /v4/login) own their error handling.
  !isOnV4ExcludedPath() &&
  !config?.sessionSafe &&
  !SESSION_SAFE_PATHS.some(p => (config?.url || '').startsWith(p));

// A live token's 401 is a permission answer — renewed only if it would end the session (clock skew).
async function v4RetryToken(config) {
  const sent = config?.v4Token;
  // A refused login is about credentials; a retry only adds a failed attempt.
  if (!sent || config.v4Retried || matchesPath(config.url || '', ['/v1/authentication'])) return null;
  const stored = getV4Token();
  if (stored !== sent) return stored;
  if (!isV4TokenExpiring(sent) && !endsV4Session(config)) return null;
  return renewV4Token(sent);
}

API.instance.interceptors.response.use(
  res => res,
  async error => {
    if (error?.response?.status !== 401) return Promise.reject(error);
    const token = await v4RetryToken(error.config);
    if (token) {
      const { config } = error;
      config.v4Retried = true;
      config.v4Token = token;
      setHeader(config, 'Authorization', `Bearer ${token}`);
      return API.instance.request(config);
    }
    if (endsV4Session(error.config)) {
      clearV4Session();
      BaseAPI.inMemoryToken = null;
      // The root login retries V4 on a V3 rejection — it serves these users too.
      window.location.href = '/?msg=sessionExpiredMessage&type=warning';
    }
    return Promise.reject(error);
  },
);

export async function loginV4(email, password) {
  const res = await API.authenticationLoginCreate({ username: email, password });
  return res.data;
}

/**
 * Resets a V4 user's password with the `token` from a reset email. Token-authorised
 * and used while logged out, so skip the securityWorker (`secure: false`), as in
 * {@link getV4PublicAlarm}. Returns the body, which carries a `return_url`.
 */
export async function resetV4Password(token, password) {
  const res = await API.authenticationResetPasswordCreate(
    { password, token },
    { secure: false },
  );
  return res.data;
}

/** Mails a V4 reset link. Logged out, so no bearer (`secure: false`). */
export async function requestV4PasswordReset(email) {
  const res = await API.authenticationRequestPasswordResetCreate(
    { email },
    { secure: false },
  );
  return res.data;
}

/**
 * The current user's authorisation rules — every account it can reach, plus the
 * locales it may translate. Null when unresolvable, which callers must read as
 * "unknown" and never as "denied".
 */
export async function getV4Permissions(params) {
  try {
    const res = await API.permissionsList(params);
    return normalizeV4Permissions(res?.data);
  } catch (_) {
    return null;
  }
}

// V3 accountId -> V4 account uuid. Cached for the life of the page since the
// mapping is stable.
const v4AccountUuidCache = new Map();

/**
 * Resolves a V3 accountId to its V4 account uuid via `migrationLookupDetail`,
 * memoising the result. Throws if no V4 account exists for the given V3 id.
 *
 * Pass `authParams` (from {@link v4AuthFor}) to use a non-default Bearer
 * token — needed when the current session token can't see the target account
 * (e.g. shadow session looking up its top account).
 */
export async function getV4AccountUuid(v3AccountId, authParams) {
  if (v4AccountUuidCache.has(v3AccountId)) return v4AccountUuidCache.get(v3AccountId);
  const res = await API.migrationLookupDetail('account:' + v3AccountId, authParams);
  const uuid = res?.data?.uuid;
  if (!uuid) throw new Error('Could not resolve V4 account for ' + v3AccountId);
  v4AccountUuidCache.set(v3AccountId, uuid);
  return uuid;
}

/**
 * If `v3AccountId` matches the original (top) admin/AM account from
 * `accountManagerUser` / `adminUser` localStorage, returns request params
 * that pin the request to that user's Bearer token. Otherwise returns
 * undefined, letting the default securityWorker token (the current session)
 * be used.
 *
 * The override sets `secure: false` so the SDK skips its `securityWorker` —
 * otherwise `mergeRequestParams` would spread the securityWorker's headers
 * AFTER ours and overwrite the Authorization back to the shadow token.
 *
 * Use when calling V4 endpoints scoped to the top account while the current
 * session is shadowed into a sub-account — the shadow token has no access
 * to the top account.
 */
export function v4AuthFor(v3AccountId) {
  try {
    const raw = localStorage.getItem('accountManagerUser') || localStorage.getItem('adminUser');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (!parsed?.token || parsed.accountId !== v3AccountId) return undefined;
    return {
      secure: false,
      headers: { Authorization: `Bearer ${parsed.token}` },
    };
  } catch (_) {
    return undefined;
  }
}

const v4DeviceUuidCache = new Map();

/**
 * V3 device id -> V4 device uuid, the SAME resolution the ARC link save runs
 * (`ensureV4DeviceUuid` -> `lookupDeviceId`); when the two disagreed the dialog showed a
 * device as unlinked while the server rejected the link as `device_already_linked`.
 * `/migration/lookup/*` DERIVES a uuid rather than looking one up, so the result is
 * verified against a real device. Positives only are cached — a negative cached before
 * an on-the-fly migration would go stale for the session. null = no V4 counterpart.
 */
export async function getV4DeviceUuid(v3DeviceId, params) {
  if (!v3DeviceId) return null;
  if (v4DeviceUuidCache.has(v3DeviceId)) return v4DeviceUuidCache.get(v3DeviceId);
  try {
    const res = await API.migrationLookupDetail(`device_${v3DeviceId}`, params);
    const uuid = res?.data?.uuid;
    if (!uuid) return null;
    await API.devicesDetail(uuid, params);
    v4DeviceUuidCache.set(v3DeviceId, uuid);
    return uuid;
  } catch (_) {
    return null;
  }
}

const v4SiteUuidCache = new Map();

// Returns null when the v3 site has no V4 counterpart. Tries both entity-string
// conventions (`site_<id>` and `site:<id>`) since the V4 backend is inconsistent.
export async function getV4SiteUuid(v3SiteId, authParams) {
  if (!v3SiteId) return null;
  if (v4SiteUuidCache.has(v3SiteId)) return v4SiteUuidCache.get(v3SiteId);
  for (const entity of [`site_${v3SiteId}`, `site:${v3SiteId}`]) {
    try {
      const res = await API.migrationLookupDetail(entity, authParams);
      const uuid = res?.data?.uuid;
      if (uuid) {
        v4SiteUuidCache.set(v3SiteId, uuid);
        return uuid;
      }
    } catch (_) { /* try the next format */ }
  }
  v4SiteUuidCache.set(v3SiteId, null);
  return null;
}

// Call after any V4 site write: misses are cached too, so a new site would stay out of the Site picker.
export function clearV4SiteUuidCache() {
  v4SiteUuidCache.clear();
}

/**
 * V3 id -> V4 uuid for an entity with no dedicated helper. `/migration/lookup/*`
 * DERIVES a uuid rather than looking one up, so it answers for entities that
 * were never migrated — V4 would accept such a uuid and silently match nothing.
 * Hence `verify` fetches the entity, the same guard `getV4DeviceUuid` applies.
 * Both entity-string conventions are tried; positives only are cached.
 */
async function lookupVerifiedV4Uuid(cache, entityType, v3Id, verify, scope) {
  if (!v3Id) return null;
  if (cache.has(v3Id)) return cache.get(v3Id);
  for (const entity of [`${entityType}_${v3Id}`, `${entityType}:${v3Id}`]) {
    try {
      // Unscoped on purpose: /v1/migration/lookup answers differently scoped.
      const res = await API.migrationLookupDetail(entity);
      const uuid = res?.data?.uuid;
      if (!uuid) continue;
      await verify(uuid, scope);
      cache.set(v3Id, uuid);
      return uuid;
    } catch (_) { /* try the next format */ }
  }
  return null;
}

const v4TeamUuidCache = new Map();
const v4GeofenceUuidCache = new Map();

/** V3 team id -> V4 team uuid, or null when the team has no V4 counterpart. */
export function getV4TeamUuid(v3TeamId, scope) {
  return lookupVerifiedV4Uuid(
    v4TeamUuidCache,
    'team',
    v3TeamId,
    (uuid, s) => API.teamsDetail(uuid, s),
    scope
  );
}

/** V3 geofence id -> V4 geofence uuid, or null when there is no counterpart. */
export function getV4GeofenceUuid(v3GeofenceId, scope) {
  return lookupVerifiedV4Uuid(
    v4GeofenceUuidCache,
    'geofence',
    v3GeofenceId,
    (uuid, s) => API.geofencesDetail(uuid, s),
    scope
  );
}

/**
 * Builds the `X-Account-Scope` request-options object for a V3 accountId.
 * Pass as the trailing options arg to any V4 SDK call that needs scoping.
 *
 *   const scope = await v4ScopeFor(device.get('accountId'));
 *   await api.usersList(scope);
 *   await api.usersCreate(payload, scope);
 */
export async function v4ScopeFor(v3AccountId) {
  const uuid = await getV4AccountUuid(v3AccountId);
  return { headers: { [SCOPE_HEADER]: uuid } };
}

/**
 * Same as {@link v4ScopeFor} but takes an already-resolved V4 account uuid —
 * use when you have the uuid in hand and want to skip the lookup.
 */
export function v4ScopeForUuid(v4Uuid) {
  return { headers: { [SCOPE_HEADER]: v4Uuid } };
}

// V4-native account ids are already uuids, which the V3 lookup rejects.
export const isV4Uuid = id => {
  const s = String(id ?? '');
  return s.length > 20 && /[^0-9]/.test(s);
};

export async function v4ScopeForAccountId(accountId) {
  return isV4Uuid(accountId) ? v4ScopeForUuid(accountId) : v4ScopeFor(accountId);
}

/**
 * Maps a V3 Contact (Immutable Record or plain object) to a V4 CreateContactRequest payload.
 * Returns null if the contact has no email (V4 requires email).
 */
export function mapV3ContactToV4(contact) {
  const js = typeof contact.toJS === 'function' ? contact.toJS() : contact;
  if (!js.email) return null;
  const nameParts = (js.name || '').trim().split(/\s+/);
  return {
    name: { first: nameParts[0] || '', last: nameParts.slice(1).join(' ') || '' },
    email: js.email,
    phones: js.phone && js.phone.length > 1 ? [js.phone] : [],
  };
}

const toUnix = iso => (iso ? Math.floor(Date.parse(iso) / 1000) || 0 : 0);

// Maps a V4 Account to the V3 model shape, uuid as the id. `created` must be
// set — the account-manager menu reads createdDate.
export function mapV4AccountToV3(v4Account) {
  return {
    id: v4Account.id,
    name: v4Account.name || '',
    type: AccountType.EndCustomer,
    created: toUnix(v4Account.created_at),
    deleted: toUnix(v4Account.deleted_at),
    archived: toUnix(v4Account.archived_at),
    modules: [],
    v4OwnerId: v4Account.owner_id || null,
    v4ManagerFlag: isV4ManagerFlagged(v4Account.settings),
    reference: storedV4AccountReference(v4Account.settings),
    language: storedV4AccountLanguage(v4Account.settings),
  };
}

// ERPNext documents key on `name`; the V3 administration models use it as the id.

export function mapV4CustomerToV3(c) {
  return {
    id: c.name || '',
    customer_name: c.customer_name || '',
    tax_id: c.tax_id || '',
  };
}

export function mapV4SalesItemToV3(i) {
  return {
    id: i.name || '',
    item_name: i.item_name || '',
    ledger: i.item_defaults?.[0]?.income_account || '',
  };
}

export function mapV4SalesInvoiceToV3(inv) {
  return {
    id: inv.name || '',
    customer: inv.customer || '',
    customer_name: inv.customer_name || '',
    posting_date: inv.posting_date || '',
    due_date: inv.due_date || '',
    net_total: inv.net_total ?? null,
    total: inv.total ?? null,
    currency: inv.currency || 'EUR',
    docstatus: inv.docstatus ?? 0,
    taxes_and_charges: inv.taxes_and_charges || '',
    payment_terms_template: inv.payment_terms_template || '',
    items: inv.items || [],
  };
}

// Keeps the uuid as the id; the grid renders phone with a leading '+', so strip it here.
export function mapV4SimCardToV3(v4Sim) {
  return {
    id: v4Sim.id,
    iccid: v4Sim.iccid || '',
    phone: (v4Sim.phone_number || '').replace(/^\+/, ''),
    communicationPlan: v4Sim.plan || '',
    jasper: v4Sim.provider || '',
  };
}

// Maps a V4 Contact to the V3 model shape, keeping the uuid as the id —
// the V4-native contact write path keys on it.
export function mapV4ContactToV3(v4Contact) {
  return {
    id: v4Contact.id,
    name: [v4Contact.name?.first, v4Contact.name?.last].filter(Boolean).join(' '),
    phone: v4Contact.phones?.[0] || '+',
    email: v4Contact.email || '',
  };
}

/**
 * Finds a V4 contact by any of the given emails, in order, case-insensitively.
 * Pass the previous email first — the V4 record still holds it after an edit.
 */
export async function findV4ContactByEmails(emails) {
  const wanted = (Array.isArray(emails) ? emails : [emails])
    .filter(Boolean)
    .map(e => String(e).trim().toLowerCase());
  if (!wanted.length) return null;
  try {
    const res = await API.contactsList();
    const contacts = res?.data || [];
    for (const email of wanted) {
      const match = contacts.find(c => c.email?.toLowerCase() === email);
      if (match) return match;
    }
    return null;
  } catch (_) {
    return null;
  }
}

/** Finds a V4 contact by a single email (case-insensitive). */
export async function findV4ContactByEmail(email) {
  return findV4ContactByEmails([email]);
}

// V4 accepts a client-supplied contact id (CreateContactRequest.id, 0.1.16). We
// derive it from the globally unique V3 id so the V3↔V4 link is computable with
// no stored mapping. The fixed prefix keeps it clear of random server UUIDs.
const V3_CONTACT_ID_PREFIX = 'c0badd1e-0000-4000-8000-';

export function v4ContactIdForV3(v3Id) {
  const n = Number(v3Id);
  if (!Number.isInteger(n) || n <= 0) return null;
  const hex = n.toString(16);
  if (hex.length > 12) return null; // beyond the 48-bit slot — unreachable for real ids
  return `${V3_CONTACT_ID_PREFIX}${hex.padStart(12, '0')}`;
}

/**
 * Upserts a V3 contact into V4 and returns the V4 contact (or null if it has no
 * email). Resolves in order: stable derived id → email (previous, then current —
 * covers email changes and pre-stable-id contacts) → create with the stable id.
 * The partial update preserves server-side fields like device_ids.
 */
export async function syncV3ContactToV4(v3Contact, previousEmail) {
  const payload = mapV3ContactToV4(v3Contact);
  if (!payload) return null;
  const js = typeof v3Contact.toJS === 'function' ? v3Contact.toJS() : v3Contact;
  const stableId = v4ContactIdForV3(js.id);

  let contacts = [];
  try {
    const res = await API.contactsList();
    contacts = res?.data || [];
  } catch (_) { /* fall through and attempt a create */ }

  const emails = [previousEmail, payload.email]
    .filter(Boolean)
    .map(e => String(e).trim().toLowerCase());
  const existing =
    (stableId && contacts.find(c => c.id?.toLowerCase() === stableId)) ||
    contacts.find(c => emails.includes(c.email?.toLowerCase())) ||
    null;

  if (existing) {
    await API.contactsUpdate(existing.id, payload);
    return { ...existing, ...payload };
  }
  const res = await API.contactsCreate(stableId ? { ...payload, id: stableId } : payload);
  return res?.data || null;
}

// V4 property endpoints aren't covered by the SDK yet; call them via the
// underlying request method so they pick up auth + the X-Account-Scope header.

export function setDeviceProperty(deviceUuid, propertyName, value, params = {}) {
  return API.request({
    path: `/v1/devices/${deviceUuid}/properties/${encodeURIComponent(propertyName)}`,
    method: 'POST',
    body: { value },
    secure: true,
    type: 'application/json',
    format: 'json',
    ...params,
  });
}

export function deleteDeviceProperty(deviceUuid, propertyName, params = {}) {
  return API.request({
    path: `/v1/devices/${deviceUuid}/properties/${encodeURIComponent(propertyName)}`,
    method: 'DELETE',
    secure: true,
    ...params,
  });
}

// Share-link endpoint — override the SDK's codegen-set `secure: true` so the
// auth header isn't sent for logged-out visitors.
export function getV4PublicAlarm(alarmUuid, originUuid) {
  return API.publicAlarmsDetail(alarmUuid, originUuid, { secure: false });
}

export default API;
